import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Modal } from 'antd';
import { IWheelRef, Wheel } from '@base/common/Wheel/Wheel';
import { IDrawResultCoupon, IWheelConfigData, ResultType } from '@base/activity/CouponDrawWheel/interface';
import { IWheelConfig } from '@base/common/Wheel/interface'
import { spinWheelDraw } from '@base/activity/CouponDrawWheel/services'
import Message from '@base/lib/Message';
import styles from './index.module.less';
import cn from 'classnames';
import delay from 'base/utils/delay'
import Iconfont from '@base/lib/Iconfont'
import { COOKIE_COUPON_WHEEL_RESULT_UNIQUE_ID } from '@base/activity/CouponDrawWheel/constant';
import { useStore } from 'base/store';
import { setCookie } from 'ava-util/dist/cookie';
import { isPhone } from 'base/utils/client';
import { Button } from '@base/lib/Button';
import { useTrans } from 'base/utils/i18n';

type IProps = {
    visible: boolean;
    onClose: () => void;
    wheelConfig?: IWheelConfigData;
    onEndDrawWheel?: (result?: IDrawResultCoupon) => void;
}

export type IWheelDrawModalRef = {
    hiddenChange: (visible?: boolean) => void;
}

const WheelDrawModal = React.forwardRef<IWheelDrawModalRef, IProps>((
    props,
    ref
) => {
    const {
        visible,
        wheelConfig,
        onClose,
        onEndDrawWheel
    } = props;

    const WHEEL_CONFIG: IWheelConfig = {
        width: isPhone ? 275 : 370,
        height: isPhone ? 275 : 370,
        fontSize1: isPhone ? 16 : 22,
        fontSize2: isPhone ? 12 : 14,
        backgroundColor: "#4E5559",
        textColor: '#ffffff',
        pointerColor: '#4E5559',
        // pointerColor: '#05DA54',
    }

    const {
        rewards = [],
    } = wheelConfig || {}

    const [spinning, setSpinning] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [result, setResult] = useState<IDrawResultCoupon>();
    const [contentVisible, setContentVisible] = useState<boolean>(false);

    const { userStore } = useStore();
    const isLogin = userStore.checkAuth();

    const { t } = useTrans();

    // Refs
    const canvasRef = useRef<IWheelRef>(null);
    const resultRef = useRef<IDrawResultCoupon>();

    // 开始旋转动画
    const startSpin = async () => {
        setSpinning(true)
        canvasRef.current?.start();
        resultRef.current = void 0;
    };

    // 停止旋转动画
    const stopSpin = async () => {
        // await delay(200)
        try {
            setFetching(true)
            const resultRes = await spinWheelDraw({
                uniqueIds: rewards.map(r => r.uniqueId) || [],
            })

            const targetIndex = rewards.findIndex(r => r.uniqueId === resultRes.uniqueId);
            console.log(resultRes)
            resultRef.current = resultRes

            if (!isLogin) {
                setCookie(COOKIE_COUPON_WHEEL_RESULT_UNIQUE_ID, resultRes.uniqueId, 1);
            }

            if (targetIndex > -1) {
                canvasRef.current?.stop(targetIndex);

            } else {
                canvasRef.current?.stop(0);
            }

        } catch (e) {
            canvasRef.current?.stop(0);
            Message.error(e.message)
        } finally {
        }
    };

    const resultHandle = () => {
        const _result = resultRef.current
        console.log({ _result })

        setResult(_result)

        onEndDrawWheel?.(_result)
        if (_result?.type === ResultType.COUPON) {
            setContentVisible(true)
        }
        setSpinning(false)
        setFetching(false)
    }

    useImperativeHandle(ref, () => ({
        hiddenChange: () => {
            setContentVisible(!contentVisible)
        },
    }))

    const initOpenHandle = async () => {
        if (rewards.length) {
            await delay(100)

            if (wheelConfig?.canDraw) {
                // 自动旋转
                startSpin().then();
            } else if (!!wheelConfig?.drawResultCoupon) {

                // 已经开过奖了
                setResult(wheelConfig.drawResultCoupon)
                const targetIndex = rewards.findIndex(r => r.uniqueId === wheelConfig?.drawResultCoupon.uniqueId);
                if (targetIndex > -1) {
                    // 定位到中奖位置
                    canvasRef.current?.reset(targetIndex);
                }
            }
        }
    }

    useEffect(() => {
        initOpenHandle().then()
    }, [visible]);

    return !(rewards.length > 0) ? null : (
        <>
            <Modal
                closable={false}
                destroyOnClose
                open={visible}
                footer={null}
                className={styles['modal-popup']}
                centered
                onCancel={onClose}
                maskClosable={false}
                maskStyle={{
                    background: 'rgba(0, 0, 0, 0.9)'
                }}
            >
                <div className={cn(
                    styles['wheel-container'],
                    contentVisible && 'g-hidden',
                )}>
                    <p
                        className={cn(
                            styles['title'],
                            'text-center',
                            { 'hidden': spinning },
                        )}>
                        {result?.type === ResultType.ONE_TIME_AGAIN
                            ? t('web_spin_try_last_chance') : (result?.type === ResultType.NO_REWARD
                                ? t('web_spin_better_luck') : (result?.type
                                    ? t('web_spin_you_get') : ''))
                        }
                    </p>

                    <Wheel
                        wheelConfig={WHEEL_CONFIG}
                        ref={canvasRef}
                        prizes={rewards}
                        onEndSpin={(reward) => {
                            resultHandle()
                        }}
                    />

                    <div
                        className={cn(
                            styles['spin-button-wrap'],
                            'flex flex-center',
                            { 'hidden': !wheelConfig?.canDraw }
                        )}
                    >

                        <div className={cn(styles['spin-button-container'])}>
                            <Button
                                loading={fetching}
                                block
                                type={'primary'}
                                className={`spin-button ${spinning ? 'spinning' : ''}`}
                                onClick={spinning ? stopSpin : startSpin}
                                disabled={fetching || result?.type === ResultType.NO_REWARD}
                            >
                                {spinning ? (
                                    <div className="flex flex-middle">
                                        <span className="spinner"/>&nbsp;
                                        <span>{t('web_spin_stop')}</span>
                                    </div>
                                ) : (
                                    <span>{
                                        result?.type === 'ONE_TIME_AGAIN'
                                            ? t('web_spin_try_last_chance') : 'Start'
                                    }</span>
                                )}
                            </Button>

                            <p className={styles['btn-tips']}>
                                {t('web_spin_disclaimer')}
                            </p>
                        </div>
                    </div>

                    <div
                        className={cn(styles['close-wrap'], 'flex flex-center')}
                    >
                        <Iconfont
                            onClick={onClose}
                            className={cn('icon-x', 'pointer')}
                        />
                    </div>
                </div>
            </Modal>

        </>
    );
});

export default WheelDrawModal;
